import { useState } from 'react';
import { Lock, Share2 } from 'lucide-react';
import { toast } from 'react-toastify';
import type { InternalLayer, LayerGrant } from 'hooks/internalLayer/types';
import {
  useLayerGrantsQuery,
  useReplaceLayerGrantsMutation,
} from 'hooks/internalLayer/useLayerGrants';
import { SaveDraftPermissionsDialog } from './SaveDraftPermissionsDialog';

export function layerIsShared(grants: LayerGrant[]) {
  return grants.some(
    (grant) =>
      grant.subjectType === 'role' && (grant.canView || grant.canEdit)
  );
}

export function LayerPrivacyControl({
  groupGuid,
  incidentId,
  scope,
  layerId,
  grants = [],
  onGrantsChange,
}: {
  groupGuid: string;
  incidentId: string | null;
  scope: 'incident' | 'hub';
  layerId?: string | null;
  grants?: LayerGrant[];
  onGrantsChange?: (grants: LayerGrant[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<LayerGrant[]>([]);
  const grantsQuery = useLayerGrantsQuery(layerId ?? '', Boolean(layerId));
  const replaceGrants = useReplaceLayerGrantsMutation();
  const currentGrants = layerId ? (grantsQuery.data ?? []) : grants;
  const shared = layerIsShared(currentGrants);

  if (layerId && !grantsQuery.isFetched) return null;
  if (layerId && grantsQuery.isError) return null;

  const confirm = async (next: LayerGrant[]) => {
    if (layerId) {
      await replaceGrants.mutateAsync({
        internalLayerId: layerId,
        grants: next,
        quiet: true,
      });
      toast.success('Permissions updated.');
    } else {
      onGrantsChange?.(next);
    }
    setOpen(false);
  };

  return (
    <span className="tw:inline-flex tw:shrink-0">
      <button
        type="button"
        className="tw:inline-flex tw:shrink-0 tw:items-center tw:gap-1 tw:bg-transparent tw:text-xs tw:font-medium tw:text-inherit"
        onClick={() => {
          setSnapshot(currentGrants);
          setOpen(true);
        }}
      >
        {shared ? (
          <Share2 className="tw:size-3.5" />
        ) : (
          <Lock className="tw:size-3.5" />
        )}
        {shared ? 'Shared' : 'Private'}
      </button>
      <SaveDraftPermissionsDialog
        open={open}
        groupGuid={groupGuid}
        scope={scope}
        incidentId={incidentId}
        pending={replaceGrants.isPending}
        initialGrants={snapshot}
        onCancel={() => {
          if (replaceGrants.isPending) return;
          setOpen(false);
        }}
        onConfirm={(next) => void confirm(next)}
      />
    </span>
  );
}

export function layerPrivacyScope(layer: Pick<InternalLayer, 'incidentId'>): {
  scope: 'incident' | 'hub';
  incidentId: string | null;
} {
  return layer.incidentId
    ? { scope: 'incident', incidentId: layer.incidentId }
    : { scope: 'hub', incidentId: null };
}
